import * as __au2ViewDef from './login-register.html';
import { IRegisterLoginService } from './../../../services/register-login-service';
import { bindable, customElement, IEventAggregator, PLATFORM } from 'aurelia';
import { INode, watch } from '@aurelia/runtime-html';
import { FieldValidationRenderer } from './../../../common/validation-renderers/field-validation-renderer';
import { ItemSelectedPropertyRule } from './../../../common/validation-matchers/is-item-selected';
import { MatchesPropertyRule } from './../../../common/validation-matchers/matches-property';
import { ValidPhoneNumberPropertyRule } from './../../../common/validation-matchers/is-valid-phone-number';
import { IValidationController } from '@aurelia/validation-html';
import { IConfigService } from './../../../services/config-service';
import { newInstanceForScope } from '@aurelia/kernel';

import { ErrorMessages, SuccessMessages } from '../../constants';
import { AddressModel } from '../../models/address-model';
import { IAppService } from '../../../services/app';
import { IValidationRules } from '@aurelia/validation';

const ALERT_SUCCESS = 'success';
const ALERT_ERROR = 'danger';

declare global {
    interface Window {
        AddressFinder: any;
    }
}
@customElement({ ...__au2ViewDef, name: 'login-register' })
export class LoginRegister {
    @bindable type;
    accountRequiresActivation = false;
    postalAddress;

    private presenter;

    private addressFinderKey;
    private appClientType;
    private showRegistrationForm = true;
    private isRegistered = false;
    private loggedin = false;
    private isActivated = false;
    private forgotPassword = false;
    private passwordReset = false;
    private registerBtnDisabled = false;
    private loginBtnDisabled = false;
    private verifyBtnDisabled = false;
    private resetBtnDisabled = false;
    private pageTitle = 'Register';
    private pdsStatementText;
    private postalAddressModel = new AddressModel();

    private alertMessage = null;
    private telephoneNumber = null;
    private email = null;
    private password = null;
    private confirm_password = null;
    private alertType = null;
    private userId = null;
    private clientCode = null;
    private verification_code = null;

    private successMessages = SuccessMessages;
    private errorMessages = ErrorMessages;

    private document: Document;
    private parent;

    private validationRules

    constructor(
        @newInstanceForScope(IValidationController)
        readonly controller: IValidationController,
        @IValidationRules validationRules: IValidationRules,
        @IAppService readonly appService: IAppService,
        @IEventAggregator readonly ea: IEventAggregator,
        @IRegisterLoginService
        readonly registerLoginService: IRegisterLoginService,
        @IConfigService readonly config: IConfigService,
        @INode readonly element: HTMLElement,
    ) {
        this.addressFinderKey = this.config.get('addressFinderKey');
        this.appClientType = this.config.get('appClientType');

        this.presenter = new FieldValidationRenderer();
        this.controller.addSubscriber(this.presenter);
        this.validationRules = validationRules
        this.initRegistrationValidation();
    }

    @watch('accountRequiresActivation')
    accountRequiresActivationChanged(newVal, oldVal) {
        if (newVal && !oldVal) {
            PLATFORM.taskQueue.queueTask(() => this.initAddressFinder());
        }
    }

    @watch('postalAddress')
    postalAddressChanged(newVal) {
        if (
            this.postalAddressModel.itemSelected &&
            this.postalAddressModel.displayText !== newVal
        ) {
            this.postalAddressModel = new AddressModel();
        }
    }

    bound(initiator, parent) {
        this.ea.subscribe('loginRequired', () => {
            this.loggedin = false;
            this.forgotPassword = false;
            this.showPaymentButton(false);
        });

        this.parent = parent;
        this.document = document;
    }

    initAddressFinder() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        const addressElement = this.element.parentElement.querySelector('#postal_address');

        if (addressElement) {
            const postalAddress = new window.AddressFinder.Widget(
                addressElement,
                this.addressFinderKey,
                this.appClientType,
                {
                    address_params: {
                        post_box: '', // Return both PO Box and regular addresses
                    },
                }
            );

            postalAddress.on('result:select', function (fullAddress, metaData) {
                self.postalAddressModel.itemSelected = true;
                self.postalAddressModel.displayText = metaData.full_address;
                self.postalAddressModel.address1 = metaData.address_line_1;
                self.postalAddressModel.address2 = metaData.address_line_2;
                self.postalAddressModel.address3 = metaData.address_line_3;
                self.postalAddressModel.suburb = metaData.locality_name;
                self.postalAddressModel.stateCode = metaData.state_territory;
                self.postalAddressModel.country = 'Australia';
                self.postalAddressModel.postCode = metaData.postcode;
                self.postalAddressModel.latitude = metaData.latitude;
                self.postalAddressModel.longitude = metaData.longitude;
            });
        }
    }

    toggleLoginRegister() {
        this.alertMessage = null;
        this.telephoneNumber = null;
        this.email = null;
        this.password = null;
        this.confirm_password = null;
        this.showRegistrationForm = !this.showRegistrationForm;
        this.pageTitle = this.showRegistrationForm ? 'Register' : 'Login';

        this.controller.reset();
        this.initLoginValidation();
    }

    initLoginValidation() {
        this.validationRules.off(this)

        this.validationRules
        .on(this)
        .ensure('email')
        .required()
        .withMessage('')
        .email()
        .ensure('password')
        .required()
        .withMessage('')
    }

    toggleForgotPassword() {
        this.alertMessage = null;
        this.password = null;
        this.passwordReset = false;
        this.forgotPassword = !this.forgotPassword;
        this.pageTitle = this.forgotPassword ? 'Reset Password' : 'Login';

        this.controller.reset();

        if(this.forgotPassword){
            this.initForgotPasswordValidation();
        }else{
            this.initLoginValidation();
        }

    }

    initForgotPasswordValidation() {
        this.validationRules.off(this)

        this.validationRules
        .on(this)
        .ensure('email')
        .required()
        .withMessage('')
    }

    initRegistrationValidation() {
        this.validationRules
            .on(this)
            .ensure('email')
            .required()
            .withMessage('')
            .email()
            .ensure('password')
            .required()
            .withMessage('')
            .ensure('confirm_password')
            .displayName('confirm password')
            .required()
            .withMessage('')
            .satisfiesRule(new MatchesPropertyRule('password'))
    }

    toggleButtonStatus(mode, isActive) {
        let btn = '';

        switch (mode) {
            case 'register':
                btn = '#registerBtn';
                this.registerBtnDisabled = !this.registerBtnDisabled;
                break;
            case 'login':
                btn = '#loginBtn';
                this.loginBtnDisabled = !this.loginBtnDisabled;
                break;
            case 'verify':
                btn = '#verifyBtn';
                this.verifyBtnDisabled = !this.verifyBtnDisabled;
                break;
            case 'reset':
                btn = '#resetBtn';
                this.resetBtnDisabled = !this.resetBtnDisabled;
                break;
        }

        const btnElement = this.element.parentElement.querySelector(btn);

        if (btnElement) {
            if (isActive) {
                btnElement.classList.add('active');
            } else {
                btnElement.classList.remove('active');
            }
        }
    }

    async login() {
        this.toggleButtonStatus('login', true);
        const result = await this.controller.validate();

        if (result.valid) {
            const loginResult = await this.registerLoginService.login(
                this.email,
                this.password
            );

            if (loginResult.status === 200) {
                $('#form-prev').hide();
                this.showPaymentButton(true);
                this.alertType = ALERT_SUCCESS;
                this.alertMessage = [
                    { message: this.successMessages.LoginSuccessful },
                ];
                this.loggedin = true;
                this.email = null;
                this.password = null;
            } else if (loginResult.status === 400) {
                this.alertType = ALERT_ERROR;

                loginResult.errors.forEach((error) => {
                    if (error.code === 'EmailNotConfirmed') {
                        this.pageTitle = 'Activate Account';
                        this.userId = error.message;
                        this.accountRequiresActivation = true;

                        this.initActivateValidation();

                        this.alertMessage = [
                            { message: this.errorMessages.EmailNotConfirmed },
                        ];
                    }

                    if (error.code === 'LoginError') {
                        this.alertMessage = [{ message: error.message }];
                    }

                    if (error.code === 'UserNotFound') {
                        this.alertMessage = [{ message: error.message }];
                    }

                    if (error.code === 'BranchInvalidForScheme') {
                        this.alertMessage = [{ message: error.message }];
                    }

                    if (error.code === 'NoSchemeGuidSet') {
                        this.alertMessage = [{ message: error.message }];
                    }
                });
            }
        }

        this.toggleButtonStatus('login', false);
    }

    async register() {
        this.toggleButtonStatus('register', true);
        const result = await this.controller.validate();

        if (result.valid) {
            const registerResult =
                await this.registerLoginService.registerEmail(
                    this.appService.scheme.schemeGuid,
                    this.email,
                    this.password
                );

            if (registerResult.status === 201) {
                this.alertType = ALERT_SUCCESS;
                this.alertMessage = [
                    { message: this.successMessages.RegistrationSuccessful },
                ];

                this.userId = registerResult.results.id;
                this.isRegistered = true;
                this.accountRequiresActivation = true;

                this.initActivateValidation();

                this.pageTitle = 'Activate Account';
                $('#form-prev').hide();
            } else if (registerResult.status === 400) {
                this.alertType = ALERT_ERROR;
                this.alertMessage = registerResult.errors;
            }
        }

        this.toggleButtonStatus('register', false);
    }

    initActivateValidation() {
        this.validationRules
            .on(this)
            .ensure('telephoneNumber')
            .satisfiesRule(new ValidPhoneNumberPropertyRule('telephoneNumber'))
            .withMessage('')
            .ensure('postalAddress')
            .satisfiesRule(new ItemSelectedPropertyRule('postalAddressModel'))
            .withMessage('');
    }

    async resendActivationCode() {
        const result = await this.registerLoginService.sendActivationCode(
            this.email
        );

        if (result.status === 200) {
            this.alertType = ALERT_SUCCESS;
            this.alertMessage = [
                { message: this.successMessages.RegistrationSuccessful },
            ];
        }
    }

    async activateRegistration() {
        this.toggleButtonStatus('verify', true);
        this.alertMessage = null;
        const result = await this.controller.validate();

        if (result.valid) {
            const verifyResult = await this.registerLoginService.activateEmail(
                this.userId,
                this.verification_code
            );

            if (verifyResult.status === 400) {
                this.alertType = ALERT_ERROR;
                this.alertMessage = [
                    { message: this.errorMessages.InvalidActivationCode },
                ];
            } else if (verifyResult.status === 200) {
                await this.registerLoginService.login(
                    this.email,
                    this.password
                );

                if (
                    this.telephoneNumber !== null &&
                    this.telephoneNumber !== ''
                ) {
                    const contactDetails = {
                        entityTypeId: 601000000,
                        countryCode: 'AU',
                        telephoneNumber: this.telephoneNumber,
                        preferredFlag: true,
                    };

                    await this.addContactDetails('phones', contactDetails);
                }

                if (this.postalAddressModel !== null) {
                    const addressObject = {
                        Addressee: '',
                        Address1: this.postalAddressModel.address1,
                        Address2: this.postalAddressModel.address2,
                        Address3: this.postalAddressModel.address3,
                        Suburb: this.postalAddressModel.suburb,
                        StateCode: this.postalAddressModel.stateCode,
                        PostCode: this.postalAddressModel.postCode,
                        Country: this.postalAddressModel.country,
                        Lat: this.postalAddressModel.latitude,
                        Lon: this.postalAddressModel.longitude,
                        EntityTypeId: 1,
                    };

                    await this.addContactDetails('addresses', addressObject);
                }

                this.password = null;
                this.confirm_password = null;
                this.isActivated = true;
                this.loggedin = true;
                this.accountRequiresActivation = false;
                this.alertType = ALERT_SUCCESS;
                this.alertMessage = [
                    { message: this.successMessages.ActivationSuccessful },
                ];
                this.showPaymentButton(true);
            }
        }

        this.toggleButtonStatus('verify', false);
    }

    async resetPassword() {
        this.toggleButtonStatus('reset', true);

        const result = await this.controller.validate();

        if (result.valid) {
            const resetResult = await this.registerLoginService.resetPassword(
                this.email
            );

            if (resetResult.status === 200) {
                this.passwordReset = true;
                this.alertType = ALERT_SUCCESS;
                this.alertMessage = [
                    { message: this.successMessages.PasswordResetSuccessful },
                ];
            } else if (resetResult.status === 400) {
                this.alertType = ALERT_ERROR;
                this.alertMessage = [
                    { message: this.errorMessages.EmailNotFound },
                ];
            }
        }

        this.toggleButtonStatus('reset', false);
    }

    showPaymentButton(show) {
        if (show) {
            $('#form-complete').css('visibility', 'visible');
        } else if (!show) {
            $('#form-complete').css('visibility', 'hidden');
        }
    }

    placesCallback(prediction) {
        this.postalAddressModel = prediction;
    }

    async addContactDetails(entityType, body) {
        await this.registerLoginService.postContactDetails(entityType, body);
    }
}

    import { Metadata as $$M } from '@aurelia/metadata';
    import { ExpressionKind as $$EK } from '@aurelia/runtime';
    import { Controller as $$C, CustomElement as $$CE, IHydrationContext as $$IHC } from '@aurelia/runtime-html';

    // @ts-ignore
    const controllers = [];

    // @ts-ignore
    if (module.hot) {

    // @ts-ignore
    module.hot.accept();

    // @ts-ignore
    const hot = module.hot;

    let aurelia = hot.data?.aurelia;

    // @ts-ignore
    document.addEventListener('au-started', (event) => {aurelia= event.detail; });
    const currentClassType = LoginRegister;

    // @ts-ignore
    const proto = LoginRegister.prototype

    // @ts-ignore
    const ogCreated = proto ? proto.created : undefined;

    if (proto) {
      // @ts-ignore
      proto.created = function(controller) {
        // @ts-ignore
        ogCreated && ogCreated.call(this, controller);
        controllers.push(controller);
      }
    }

    // @ts-ignore
    hot.dispose(function (data) {
      // @ts-ignore
      data.controllers = controllers;
      data.aurelia = aurelia;
    });

    if (hot.data?.aurelia) {
      const newDefinition = $$CE.getDefinition(currentClassType);
      $$M.define(newDefinition.name, newDefinition, currentClassType);
      $$M.define(newDefinition.name, newDefinition, newDefinition);
      hot.data.aurelia.container.res[$$CE.keyFrom(newDefinition.name)] = newDefinition;

      const previousControllers = hot.data.controllers ?? [];
      if(previousControllers.length === 0) {
        // @ts-ignore
        hot.invalidate?.();
      }

      // @ts-ignore
      previousControllers.forEach(controller => {
        const values = { ...controller.viewModel };
        const hydrationContext = controller.container.get($$IHC)
        const hydrationInst = hydrationContext.instruction;

        const bindableNames = Object.keys(controller.definition.bindables);
        // @ts-ignore
        Object.keys(values).forEach(key => {
          if (bindableNames.includes(key)) {
            return;
          }
          // if there' some bindings that target the existing property
          // @ts-ignore
          const isTargettedByBinding = controller.bindings?.some(y =>
            y.ast?.$kind === $$EK.AccessScope
              && y.ast.name === key && y.targetProperty
          );
          if (!isTargettedByBinding) {
            delete values[key];
          }
        });
        const h = controller.host;
        delete controller._compiledDef;
        controller.viewModel = controller.container.invoke(currentClassType);
        controller.definition = newDefinition;
        Object.assign(controller.viewModel, values);
        if (controller._hydrateCustomElement) {
          controller._hydrateCustomElement(hydrationInst, hydrationContext);
        } else {
          controller.hE(hydrationInst, hydrationContext);
        }
        h.parentNode.replaceChild(controller.host, h);
        controller.hostController = null;
        controller.deactivate(controller, controller.parent ?? null, 0);
        controller.activate(controller, controller.parent ?? null, 0);
      });
    }
  }